import React, { useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    errorMessage,
    formatDate,
    formatStatus,
    formatUsername,
} from "../../../util/Helpers";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import cogoToast from "cogo-toast";
import { Permissions, PermissionType } from "../../../util/Permissions";
import ViewPortfolioCreationModal from "./modals/ViewPortfolioCreationModal";
import EditPortfolioCreationModal from "./modals/EditPortfolioCreationModal";
import { FilterGroup } from "../../Common/components/FilterGroup";
import { Permission } from "../../Common/layout/Permission"
import { EditIcon, ViewIcon } from "../../../util/Icons";
import { useDownloadPortfolioCreationReportMutation } from "../../../../redux/apiSlice";

export const PortfolioCreationManagement = (
    {
        match: {
            params: { userGroup },
        },
    }
) => {
    // Portfolio Creation Redux state
    const tableRef = useRef();

    /* PortfolioCreation Modal State Management */
    const [portfolioCreationModal, setPortfolioCreationModal] = useState({
        showViewPortfolioCreationModal: false,
        showEditPortfolioCreationModal: false,
        details: null
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setPortfolioCreationModal({ ...portfolioCreationModal, [modal]: !portfolioCreationModal[modal] })
    }

    /* View PortfolioCreation Details */
    const viewPortfolioCreation = details => {
        setPortfolioCreationModal({
            ...portfolioCreationModal,
            showViewPortfolioCreationModal: true,
            details
        })
    }

    /* Edit PortfolioCreation Modal*/
    const editPortfolioCreation = details => {
        setPortfolioCreationModal({
            ...portfolioCreationModal,
            showEditPortfolioCreationModal: true,
            details
        })
    }

    const [
        downloadPortfolioCreationReport,
        {
            isLoading: isLoadingReport,
            isSuccess: isReportGenerating,
            error: errorReport,
        }
    ] = useDownloadPortfolioCreationReportMutation()

    useEffect(() => {
        if (errorReport) {
            cogoToast.error(errorReport.data);
        }
    }, [errorReport])

    useEffect(() => {
        if (isReportGenerating) {
            cogoToast.success(`Portfolio creation report export in progress. The resulting file will be sent to your mail.`);
        }
    }, [isReportGenerating])


    /* Action Buttons */
    const portfolioCreationAction = (row) => {
        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Button
                    title="View PortfolioCreation"
                    className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                    onClick={_ => viewPortfolioCreation(row)}
                >
                    <span className="svg-icon svg-icon-md svg-icon-primary">
                        <ViewIcon />
                    </span>
                </Button>

                <Permission permission={Permissions.ACCOUNTS} permissionType={PermissionType.UPDATE}>
                    <Button
                        title="Edit PortfolioCreation"
                        className={`btn btn-icon btn-light btn-hover-primary btn-sm mx-3`}
                        onClick={_ => editPortfolioCreation(row)}
                        disabled={(row.status === "COMPLETED" && row.paymentStatus === "COMPLETED" && row.accountFundStatus === "COMPLETED")}
                    >
                        <span className={`svg-icon svg-icon-md svg-icon-${(row.status !== "FAILED") ? "muted" : "primary"}`}>
                            <EditIcon />
                        </span>
                    </Button>
                </Permission>
            </span>
        );
    };

    return (
        <div className="py-4 px-6">
            <FilterGroup
                key={userGroup}
                defaultFilters={{ endDate: new Date().toISOString().split('T')[0] }}
            >
                <FilterGroup.DateRange className={"mb-4"} />
                <div className="d-flex mb-4">
                    <div className={"mr-4"}>
                        <FilterGroup.Filter name={"status"} label={"Status"}>
                            <FilterGroup.Option value="">All</FilterGroup.Option>
                            <FilterGroup.Option value="PENDING">PENDING</FilterGroup.Option>
                            <FilterGroup.Option value="COMPLETED">COMPLETED</FilterGroup.Option>
                            <FilterGroup.Option value="FAILED">FAILED</FilterGroup.Option>
                        </FilterGroup.Filter>
                    </div>
                </div>
                <FilterGroup.Content>
                    {
                        ({ filters }) => {
                            return <Card>
                                <CardHeader title={`Portfolio Creation`} >
                                    <Permission permission={Permissions.REPORTS} permissionType={PermissionType.CREATE}>
                                        <CardHeaderToolbar>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={isLoadingReport}
                                                onClick={() => downloadPortfolioCreationReport({
                                                    userGroup,
                                                    status: filters?.status || "",
                                                    startDate: filters?.startDate || "",
                                                    endDate: filters?.endDate || "",
                                                    format: "csv",
                                                })}
                                            >
                                                Export CSV
                                            </button>
                                        </CardHeaderToolbar>
                                    </Permission>

                                </CardHeader>
                                <CardBody>

                                    <DynamicSimpleTable
                                        key={userGroup}
                                        queryParams={
                                            `userGroup=${userGroup}`
                                            + `&status=${filters?.status || ""}`
                                            + `&startDate=${filters?.startDate || ""}`
                                            + `&endDate=${filters?.endDate || ""}`
                                        }
                                        searchBox={true}
                                        source={{ url: `/portfolio-creation` }}
                                        columns={[
                                            { title: "ID", key: "id" },
                                            { title: "USER", format: row => formatUsername(row.userId, row.username, null, null, null) },
                                            { title: "PARTNER", key: "partner" },
                                            { title: "AMOUNT", key: "amount" },
                                            { title: "PAYMENT STATUS", format: (row) => formatStatus(row.paymentStatus) },
                                            { title: "ACCOUNT CREATED", format: (row) => (!!row.accountCreated).toString() },
                                            { title: "ACCOUNT FUND STATUS", format: (row) => formatStatus(row.accountFundStatus) },
                                            { title: "AMOUNT FUNDED", key: "amountFunded" },
                                            { title: "STATUS", format: (row) => formatStatus(row.status) },
                                            { title: "CREATED", format: (row) => formatDate(row.created) },
                                            { title: "UPDATED", format: (row) => formatDate(row.updated) },
                                            { title: "", format: portfolioCreationAction }
                                        ]}
                                        ref={tableRef}
                                        onError={(err) =>
                                            cogoToast.error(errorMessage(err))
                                        }
                                        refreshIcon={true}
                                    />
                                </CardBody>
                            </Card>
                        }
                    }
                </FilterGroup.Content>
            </FilterGroup>

            <ViewPortfolioCreationModal
                {...portfolioCreationModal}
                onHide={toggleModal("showViewPortfolioCreationModal")}
            />
            {
                portfolioCreationModal.showEditPortfolioCreationModal &&
                <EditPortfolioCreationModal
                    {...portfolioCreationModal}
                    onHide={toggleModal("showEditPortfolioCreationModal")}
                    tableRef={tableRef}
                />
            }
        </div>
    );
};
